import { Link } from "react-router";
import { listProfiles, listWorkbooks } from "@/lib/api/endpoints";
import { InlineArrow } from "@/components/icons/InlineArrow";
import { readActiveCustomerFromDocument } from "@/lib/mapper/active-customer-cookie";
import { PageError, PageLoading, useLoader } from "@/lib/loader";
import { NewWorkbookDialog } from "./NewWorkbookDialog";

function formatDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getDetectedType(detected: unknown): string {
  if (typeof detected !== "object" || detected === null) return "Unknown";
  const entries = Object.entries(detected);
  if (entries.length === 0) return "Unknown";
  const [, value] = entries[0];
  if (typeof value === "object" && value !== null && "type" in value) {
    return (value as { type: string }).type || "Unknown";
  }
  return "Unknown";
}

function getSheetCount(sheets: unknown): number {
  if (Array.isArray(sheets)) {
    return sheets.length;
  }
  return 0;
}

export function WorkbooksPage() {
  const loaded = useLoader(
    () =>
      Promise.all([
        listWorkbooks(),
        listProfiles({ customerId: readActiveCustomerFromDocument() }),
      ]),
    [],
  );
  if (loaded.status === "error") return <PageError error={loaded.error} />;
  if (!loaded.data) return <PageLoading />;
  const [{ workbooks }, profiles] = loaded.data;
  const templates = profiles.map((profile) => ({
    id: profile.id,
    name: profile.name,
    productType: profile.productType,
    type: profile.type,
    isDefault: profile.isDefault,
  }));

  return (
    <div className="mx-auto max-w-7xl p-5 lg:p-6">
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <h2 className="text-xl font-semibold text-slate-950 dark:text-slate-100">Workbooks</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {workbooks.length} workbook{workbooks.length === 1 ? "" : "s"} · newest first
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <NewWorkbookDialog profiles={templates} />
          <Link
            to="/upload"
            className="inline-flex w-fit items-center rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
          >
            Upload a file
          </Link>
        </div>
      </div>

      {workbooks.length === 0 ? (
        <div className="rounded border border-dashed border-slate-300 dark:border-border bg-white dark:bg-surface p-10 text-center">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">No workbooks yet</p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Upload an Excel file to create your first workbook.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <NewWorkbookDialog
              profiles={templates}
              triggerLabel="New workbook"
            />
            <Link
              to="/upload"
              className="inline-flex rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
            >
              Upload now
            </Link>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-slate-200 dark:border-border bg-white dark:bg-surface">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-sm">
              <thead className="bg-slate-100 dark:bg-surface-2 text-xs uppercase text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2 font-semibold">File</th>
                  <th className="px-4 py-2 font-semibold">Sheets</th>
                  <th className="px-4 py-2 font-semibold">Type</th>
                  <th className="px-4 py-2 font-semibold">Created</th>
                  <th className="px-4 py-2 text-right font-semibold">Open</th>
                </tr>
              </thead>
              <tbody>
                {workbooks.map((workbook) => (
                  <tr key={workbook.id} className="border-t border-slate-100 dark:border-border hover:bg-slate-50 dark:hover:bg-surface-2">
                    <td className="px-4 py-3">
                      <Link
                        to={`/workbooks/${workbook.id}`}
                        className="font-medium text-slate-900 dark:text-slate-100 hover:underline"
                      >
                        {workbook.originalName}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                      {getSheetCount(workbook.sheets)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">
                        {getDetectedType(workbook.detected)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">
                      {formatDate(workbook.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/workbooks/${workbook.id}`}
                        className="text-sm font-medium text-slate-700 dark:text-slate-300 hover:underline"
                      >
                        Open <InlineArrow />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
